// Pure lifecycle helpers for the fullscreen voice stage (§9.5 / Epic 12 QA).
//
// The fullscreen stage must survive EVERY in-session transient. Tool execution,
// update_persona / update_avatar results, transcript deltas, turn boundaries,
// interruptions and (transparent) reconnects all keep it open. It comes down
// only when the session is genuinely over, or when a flow navigates away.

// Orb statuses in which the session is still live. "reconnecting" is live: the
// upstream dropped (go_away) and is being restored on the SAME client socket.
export const LIVE_STATUSES = new Set([
  "connecting", "listening", "speaking", "thinking", "tool", "reconnecting",
]);

export function isLiveStatus(status) {
  return LIVE_STATUSES.has(status);
}

const LIVE_PHASE = {
  connecting: "conectando",
  listening: "ouvindo você",
  speaking: "falando com você",
  thinking: "pensando",
  tool: "trabalhando",
  reconnecting: "reconectando",
};

export function voiceLiveCopy(status) {
  const phase = LIVE_PHASE[status] || "no ar";
  return {
    title: `Conversa ao vivo · ${phase}`,
    hint: "Não é uma gravação. Pode falar à vontade.",
  };
}

// Session-active lifecycle for the stage. `active` is TRUE from the instant
// start() is called until a DEFINITIVE end — a user stop, the socket closing
// without a reconnect, or a fatal error frame. A transparent reconnect MUST NOT
// zero it, so the stage keeps showing "Reconectando" instead of collapsing and
// the user never has to reopen it mid-conversation.
export function nextSessionActive(prev, action) {
  switch (action) {
    case "start": return true;   // a session is being opened
    case "stop":  return false;  // user end / socket closed with no reconnect
    case "fatal": return false;  // error frame — the session is over
    default:      return prev;    // reconnecting / status blips: unchanged
  }
}

// Decide whether a live voice event must tear down the fullscreen stage. Only a
// flow that navigates away closes it:
//   - request_review      -> "navigate" (avatar goes to the review screen)
//   - finalize_onboarding -> "drain"    (persona celebrates, then routes on)
// Everything else (tool execution, update_persona/update_avatar, transcript,
// turn_complete, interrupted, artifact, reconnecting) keeps the stage open.
// A definitive session end (socket closed / fatal error) is handled by the
// session-active lifecycle above, not here.
export function shouldCloseFullscreen(evt) {
  if (!evt || evt.type !== "tool_result" || !evt.result || evt.result.ok !== true) {
    return { close: false, mode: null };
  }
  if (evt.name === "request_review") return { close: true, mode: "navigate" };
  if (evt.name === "finalize_onboarding") return { close: true, mode: "drain" };
  return { close: false, mode: null };
}
